import { useGetOneFormSchema } from "#src/components/hooks/adapters/useFormSchema";
import {
  Button,
  Card,
  Dialog,
  DropdownInput,
  EmptyState,
  Form,
  Icon,
  RadioInput,
  TextInput,
  useForm,
  useFormContext,
} from "@validereinc/common-components";
import { FormSubmissionTaskType } from "@validereinc/domain";
import { toStartCaseString } from "@validereinc/utilities";
import { produce } from "immer";
import React, { useEffect, useMemo, useState } from "react";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";
import {
  applyOutputVariableFormSubmission,
  formSchemaIdMatchesWorkflowPathSegment,
  formSchemaSectionIdMatchesPathSegment,
  normalizeFormSubmissionAnswerOutputVariables,
  matchFormSubmissionAnswerOutputPath,
} from "./WorkflowConfigurationOutputVariableForm.helpers";

const fieldsSupportedAsFormSubmissionOutput = ["id"];

type FormValuesType = {
  variableSourceType?: "primaryFields" | "formSubmissionAnswers";
  answerSectionId?: string;
  answerSectionIndex?: string;
  answerQuestionId?: string;
  primaryField?: string;
  variableName: string;
};

const createOutputVariableFormDefaultValues = (): FormValuesType => ({
  variableSourceType: undefined,
  answerSectionId: undefined,
  answerSectionIndex: "0",
  answerQuestionId: undefined,
  primaryField: undefined,
  variableName: "",
});

const VariableCard = ({
  variableName,
  variableValue,
  formSchemaId,
  removeVariable,
}: {
  variableName: string;
  variableValue: string;
  formSchemaId: string;
  removeVariable: (variableName: string) => void;
}) => {
  const formSchemaQuery = useGetOneFormSchema(
    {
      id: formSchemaId,
    },
    { enabled: !!formSchemaId }
  );

  let value = variableValue;
  if (variableValue.startsWith("$.answers")) {
    const parsedPath = matchFormSubmissionAnswerOutputPath(variableValue);
    if (!parsedPath) return null;
    const { sectionId, sectionIndex, questionId } = parsedPath;
    const section = formSchemaQuery.data?.data.config.sections.find((section) =>
      formSchemaSectionIdMatchesPathSegment(section.id, sectionId)
    );

    const sectionName = section?.name ?? "";

    const questionPrompt =
      Object.entries(formSchemaQuery.data?.data?.config.questions ?? {}).find(
        ([id]) => formSchemaIdMatchesWorkflowPathSegment(id, questionId)
      )?.[1]?.prompt ?? "";

    const values: string[] = [];

    values.push(`Section Name: ${sectionName}`);

    if (section?.is_repeatable) values.push(`Section Index: ${sectionIndex}`);

    values.push(`Question: ${questionPrompt}`);
    value = values.join(", ");
  } else if (
    variableValue.startsWith("$.") &&
    fieldsSupportedAsFormSubmissionOutput.includes(
      variableValue.replace("$.", "")
    )
  ) {
    value = `Form Submission ${toStartCaseString(variableValue.replace("$.", ""))}`;
  }
  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <b style={{ fontFamily: "monospace" }}>{variableName}</b> is mapped to{" "}
          <br />
          <b>{value}</b>
        </div>
        <Button
          icon="minus-circle"
          variant="error-outline"
          style={{ width: "fit-content" }}
          onClick={() => {
            removeVariable(variableName);
          }}
        />
      </div>
    </Card>
  );
};

const CreateOutputVariableDialogFormFields = ({
  formSchema,
  localVariables,
}: {
  formSchema?: ReturnType<typeof useGetOneFormSchema>["data"];
  localVariables: Record<string, string>;
}) => {
  const { watch, setValue } = useFormContext<FormValuesType>();
  const variableSourceType = watch("variableSourceType");
  const selectedSectionId = watch("answerSectionId");

  useEffect(() => {
    if (!selectedSectionId) return;

    setValue("answerQuestionId", "");
  }, [selectedSectionId, setValue]);

  const sectionsList =
    formSchema?.data.config.sections.map((section) => ({
      label: section.name,
      value: section.id,
    })) ?? [];

  const questionsList = (
    formSchema?.data.config.sections.find(
      (section) => section.id === selectedSectionId
    )?.questions ?? []
  ).map((value) => ({
    label: formSchema?.data.config?.questions?.[value]?.prompt ?? "",
    value,
  }));

  const fieldsList = fieldsSupportedAsFormSubmissionOutput.map((value) => ({
    label: `Form Submission "${value}"`,
    value,
  }));

  const isSelectedSectionRepeatable = useMemo(() => {
    const sectionDefinition = formSchema?.data.config.sections.find(
      (section) => section.id === selectedSectionId
    );

    if (!sectionDefinition) return false;

    return sectionDefinition.is_repeatable;
  }, [formSchema, selectedSectionId]);

  const showMetadataFields = variableSourceType === "primaryFields";
  const showAnswerFields = variableSourceType === "formSubmissionAnswers";

  return (
    <>
      <RadioInput
        isRequired
        label="Data Source"
        name="variableSourceType"
        options={[
          { label: "Form Submission Metadata", value: "primaryFields" },
          { label: "Form Submission Answers", value: "formSubmissionAnswers" },
        ]}
        labelKey="label"
        valueKey="value"
      />

      {showMetadataFields && (
        <DropdownInput
          isRequired
          noSearchDebounce
          name="primaryField"
          options={fieldsList}
          labelKey="label"
          valueKey="value"
          label="Metadata field"
        />
      )}
      {showAnswerFields && (
        <>
          <DropdownInput
            isRequired
            noSearchDebounce
            name="answerSectionId"
            options={sectionsList}
            labelKey="label"
            valueKey="value"
            label="Section"
            isClearable={false}
          />
          {isSelectedSectionRepeatable && (
            <TextInput
              isRequired
              name="answerSectionIndex"
              label="Section Index (number / variable)"
            />
          )}
          <DropdownInput
            isRequired
            noSearchDebounce
            name="answerQuestionId"
            options={questionsList}
            labelKey="label"
            valueKey="value"
            label="Question"
            isClearable={false}
            isDisabled={!selectedSectionId}
          />
        </>
      )}

      {variableSourceType && (
        <TextInput
          label="Variable Name"
          name="variableName"
          isRequired
          rules={{
            validate: {
              validateVariable: (value: string) =>
                value && /^\$[A-Z][A-Z0-9_]*$/.test(value)
                  ? true
                  : "Incorrect Variable Format",
              validateExistingVariable: (value: string) =>
                Object.keys(localVariables).includes(value)
                  ? "This variable already exists for this step."
                  : true,
            },
          }}
        />
      )}
    </>
  );
};

const WorkflowConfigurationOutputVariableForm = ({
  stepDefinition,
  selectedNodeId,
}: {
  stepDefinition: FormSubmissionTaskType;
  selectedNodeId?: string;
}) => {
  const formSchemaQuery = useGetOneFormSchema(
    {
      id: stepDefinition.form_schema_id,
    },
    { enabled: !!stepDefinition.form_schema_id }
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [localVariables, setLocalVariables] = useState<Record<string, string>>(
    () =>
      normalizeFormSubmissionAnswerOutputVariables(stepDefinition.output ?? {})
  );

  useEffect(() => {
    setLocalVariables(
      normalizeFormSubmissionAnswerOutputVariables(stepDefinition.output ?? {})
    );
  }, [JSON.stringify(stepDefinition.output)]);

  const form = useForm<FormValuesType>({
    defaultValues: createOutputVariableFormDefaultValues(),
  });

  const resetOutputVariableDialogForm = () => {
    form.reset(createOutputVariableFormDefaultValues());
  };

  const { setOutput } = useWorkflowConfigurationFormStore();

  useEffect(() => {
    setOutput(localVariables);
  }, [JSON.stringify(localVariables)]);

  const handleSubmit = (data: FormValuesType) => {
    if (!selectedNodeId) return;

    const nextVariables = applyOutputVariableFormSubmission(
      localVariables,
      data
    );
    if (!nextVariables) return;

    setLocalVariables(nextVariables);
    resetOutputVariableDialogForm();
    setIsDialogOpen(false);
  };

  const variables = Object.entries(localVariables);

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {variables.length === 0 && (
          <EmptyState
            title="No output variables created"
            suggestion="Create output variables from selected form questions and answers to re-use in the workflow template."
            icon={<Icon variant="files" />}
            style={{ padding: 16 }}
          />
        )}

        {variables.map((variable) => (
          <VariableCard
            key={variable[0]}
            variableName={variable[0]}
            variableValue={variable[1]}
            formSchemaId={stepDefinition.form_schema_id}
            removeVariable={(variableName) => {
              setLocalVariables((currentValue) =>
                produce(currentValue, (draft) => {
                  delete draft[variableName];
                })
              );
            }}
          />
        ))}

        <Button
          icon="plus-circle"
          style={{ width: "100%" }}
          onClick={() => setIsDialogOpen(true)}
        >
          Create Output Variables
        </Button>
      </div>

      <Dialog
        isOpen={isDialogOpen}
        onClose={() => {
          resetOutputVariableDialogForm();
          setIsDialogOpen(false);
        }}
        title="Create Output Variable"
        actionRow={[
          <Button
            key="create"
            variant="primary"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={form.handleSubmit(handleSubmit)}
            disabled={!form.formState.isValid}
          >
            Create
          </Button>,
        ]}
      >
        <Form {...form}>
          <CreateOutputVariableDialogFormFields
            formSchema={formSchemaQuery.data}
            localVariables={localVariables}
          />
        </Form>
      </Dialog>
    </>
  );
};

export default WorkflowConfigurationOutputVariableForm;
